import React, { useEffect, useState } from 'react';
import AppShell from '@/components/layout/AppShell';
import { ownerNav } from '@/lib/navConfig';
import KPICard from '@/components/common/KPICard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Wallet, Package, ShoppingBag, TrendingUp, AlertCircle } from 'lucide-react';
import api, { fmtIDR, currentMonth } from '@/lib/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';

export default function OwnerDashboard() {
  const [summary, setSummary] = useState(null);
  const [products, setProducts] = useState([]);
  const [daily, setDaily] = useState([]);
  const [missingHpp, setMissingHpp] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [s, p, d, mh] = await Promise.all([
          api.get(`/reports/sales-summary?month=${currentMonth()}`),
          api.get('/products?active_only=false'),
          api.get(`/reports/daily-sales?month=${currentMonth()}`),
          api.get('/products?only_missing_hpp=true'),
        ]);
        setSummary(s.data);
        setProducts(p.data);
        setDaily(d.data);
        setMissingHpp(mh.data);
      } catch (e) {
        console.error('[OwnerDashboard] gagal load data dashboard:', e);
      }
    };
    load();
  }, []);

  const totalStock = products.reduce((a, p) => a + (p.stock || 0), 0);
  const lowStock = products.filter((p) => p.stock <= (p.low_stock_threshold || 5)).length;

  return (
    <AppShell nav={ownerNav} title="Dashboard Owner" showBell>
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <KPICard label="Omset Bulan Ini" value={fmtIDR(summary?.revenue || 0)} icon={Wallet} tone="info" testId="kpi-revenue" />
          <KPICard label="COGS (HPP Terjual)" value={fmtIDR(summary?.cogs || 0)} icon={ShoppingBag} tone="warning" testId="kpi-cogs" />
          <KPICard label="Laba Kotor" value={fmtIDR(summary?.gross_profit || 0)} icon={TrendingUp} tone="success" testId="kpi-profit" />
          <KPICard label="Total Stok" value={totalStock.toLocaleString('id-ID')} sub={`${products.length} SKU aktif`} icon={Package} tone="default" testId="kpi-stock" />
        </div>

        {missingHpp.length > 0 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center"><AlertCircle className="h-5 w-5" /></div>
                <div className="flex-1">
                  <div className="font-medium">Ada {missingHpp.length} barang tanpa HPP</div>
                  <div className="text-sm text-muted-foreground">Segera input HPP agar laporan laba akurat.</div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {missingHpp.slice(0, 6).map((p) => (
                      <Link key={p.id} to={`/owner/products?highlight=${p.id}`} data-testid={`missing-hpp-${p.id}`}>
                        <Badge variant="secondary" className="cursor-pointer hover:bg-amber-100">{p.name}</Badge>
                      </Link>
                    ))}
                    {missingHpp.length > 6 && <Badge>+{missingHpp.length - 6} lagi</Badge>}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2">
            <CardHeader><CardTitle className="font-heading">Penjualan Harian Bulan Ini</CardTitle></CardHeader>
            <CardContent>
              {daily.length === 0 ? (
                <div className="h-64 flex items-center justify-center text-sm text-muted-foreground">Belum ada data penjualan</div>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={daily} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d?.slice(8, 10)} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1e6).toFixed(0)}jt`} />
                    <Tooltip formatter={(v) => fmtIDR(v)} labelFormatter={(l) => `Tanggal ${l}`} />
                    <Bar dataKey="revenue" fill="hsl(186 86% 28%)" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="font-heading">Ringkasan</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <Row label="Transaksi" value={summary?.transactions || 0} />
              <Row label="Stok Menipis" value={lowStock} tone={lowStock > 0 ? 'warning' : ''} />
              <Row label="Total SKU" value={products.length} />
              <Row label="Barang Perlu HPP" value={missingHpp.length} tone={missingHpp.length > 0 ? 'warning' : ''} />
            </CardContent>
          </Card>
        </div>
      </div>
    </AppShell>
  );
}

function Row({ label, value, tone }) {
  return (
    <div className="flex items-center justify-between border-b last:border-0 pb-2 last:pb-0">
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className={`font-heading font-semibold tabular-nums ${tone === 'warning' ? 'text-amber-600' : ''}`}>{value}</div>
    </div>
  );
}
